import { useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle, MagnifyingGlass, SealCheck, ArrowRight } from "@phosphor-icons/react";
import { getUtm } from "@/components/AttendeeFields";
import { useFetch } from "@/lib/useFetch";
import { api, errMsg, money, fmtDate } from "@/lib/api";
import { PageHero, Section, SectionHead, Loader } from "@/components/Blocks";
import { RiskNotice } from "@/components/layout/PublicLayout";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { IndicatorAccess } from "@/components/IndicatorCard";
import { PriceCards, IndicatorNotice } from "@/components/PriceCards";

const IndicatorBuy = ({ testid }) => (
  <div className="panel mt-10 p-6" data-testid={testid}>
    <h3 className="font-display text-2xl font-bold uppercase">The Mission Control Indicator</h3>
    <p className="mb-4 mt-1 text-sm text-[#475569]">Sold through TradingView, for lecture graduates only.</p>
    <IndicatorNotice testid="indicator-notice-indicator-page" className="mb-4" />
    <IndicatorAccess />
  </div>
);

export function Pricing() {
  const { data } = useFetch("/pricing");
  const p = data?.prices || {};
  const tiers = data ? [{ code: "early", name: "Early-bird", price_cents: p.early, on_sale: true }, { code: "regular", name: "Regular", price_cents: p.regular, on_sale: true },
    { code: "vip", name: "VIP", price_cents: p.vip, capacity: p.vip_capacity, on_sale: !!data.vip_benefits, reason: data.vip_benefits ? "" : "vip_benefits_missing" }] : [];
  return (
    <>
      <PageHero eyebrow="Pricing" title="One day. Five markets." sub="Every ticket includes the full-day class, lunch, two coffee breaks, the course binder, student app access, quizzes, badges and the certificate path. Prices in US dollars, plus applicable taxes." />
      <Section>
        <div data-testid="pricing-cards"><PriceCards tiers={tiers} vipBenefits={data?.vip_benefits} /></div>
        <div className="mt-10 grid gap-4 md:grid-cols-2">
          <div className="panel p-6"><h3 className="font-display text-2xl font-bold uppercase">Groups and companies</h3><p className="mt-2 text-sm text-[#475569]">Booking 5 or more seats? Contact us for group pricing and one invoice.</p><Link to="/contact" className="btn-outline mt-4">Contact sales</Link></div>
          <div className="panel p-6"><h3 className="font-display text-2xl font-bold uppercase">Not sure yet?</h3><p className="mt-2 text-sm text-[#475569]">Join a free intro seminar first and see how the course works.</p><Link to="/free-seminar" className="btn-outline mt-4">Free intro seminar</Link></div>
        </div>
        <IndicatorBuy testid="pricing-indicator" />
        <p className="mt-6 text-xs text-[#475569]">Early-bird ends at 11:59 p.m. local time, 30 days before January and February classes and 60 days before all other classes, then Regular applies. At the door: {money(p.regular)} while seats remain. VIP is limited to {p.vip_capacity || 50} seats per city.</p>
        <RiskNotice className="mt-4" />
      </Section>
    </>
  );
}

const TF = [["1 D", "Daily target"], ["5 HR", "Swing target"], ["13 MIN", "Intraday target"], ["1 MIN", "Scalp target"]];

export function MissionControl() {
  return (
    <>
      <PageHero eyebrow="The program" title="The Mission Control method" sub="A chart tool and a calm process. Pull down the range, read the targets per time frame, and always know where the stop is before you launch." />
      <Section tone="dark" className="relative noise">
        <div className="grid items-center gap-10 lg:grid-cols-12">
          <img src="/brand/hero_art.jpg" alt="Mission Control indicator showing range, targets per time frame and stop loss" className="w-full rounded-lg border border-[#E0A21B]/20 lg:col-span-7" />
          <div className="space-y-3 lg:col-span-5">
            {TF.map(([k, v], i) => (
              <div key={k} className="dark-panel rise flex items-center justify-between p-4" style={{ animationDelay: `${i * 80}ms` }}>
                <span className="rounded bg-[#1E8E5A] px-3 py-1 font-mono-num text-sm font-semibold text-white">{k}</span>
                <span className="text-white/75">{v}</span>
              </div>
            ))}
            <div className="dark-panel flex items-center justify-between border-[#C8382E]/40 p-4">
              <span className="rounded bg-[#C8382E] px-3 py-1 font-mono-num text-sm font-semibold text-white">SL</span>
              <span className="text-white/75">Stop loss: respect the landing</span>
            </div>
          </div>
        </div>
      </Section>
      <Section tone="white">
        <SectionHead eyebrow="Mission table" title="What the indicator shows" />
        <div className="grid gap-4 md:grid-cols-3">
          {[["Range pulldown", "Draws the active range so you can see where price is inside it."], ["Targets per time frame", "Shows take-profit levels from 1 minute to 6 months."], ["Bias and probability", "A simple read on direction, shown with honest probability, never a promise."]].map(([t, d]) => (
            <div key={t} className="panel p-6"><h3 className="font-display text-2xl font-bold uppercase">{t}</h3><p className="mt-2 text-sm text-[#475569]">{d}</p></div>
          ))}
        </div>
        <IndicatorBuy testid="program-indicator" />
        <div className="mt-8 flex flex-wrap gap-3"><Link to="/cities" className="btn-gold" data-testid="program-find-class-btn">Find a class <ArrowRight /></Link><Link to="/courses/mission-control" className="btn-outline">Full curriculum</Link></div>
        <RiskNotice className="mt-6" />
      </Section>
    </>
  );
}

export function About() {
  return (
    <>
      <PageHero eyebrow="Your instructor" title="[Instructor name]" sub="[Placeholder bio: add your instructor background, approach and credentials in a future edit.]" />
      <Section tone="white">
        <div className="grid gap-10 lg:grid-cols-12">
          <div className="flex aspect-[4/5] items-center justify-center rounded-lg bg-[#0B1B33] text-white/40 lg:col-span-5" data-testid="instructor-photo-placeholder">[Instructor photo]</div>
          <div className="space-y-5 lg:col-span-7">
            <SectionHead eyebrow="Approach" title="Calm, clear, risk first" />
            <p className="text-[#475569]">Mission Control Trading is taught in plain language. Every lesson starts with risk, then structure, then the setup. Students practise on demo accounts before they ever use real money.</p>
            <p className="text-[#475569]">[Placeholder: years of experience, markets traded, teaching history and any verifiable credentials.]</p>
            <RiskNotice />
          </div>
        </div>
      </Section>
    </>
  );
}

export function FreeSeminar() {
  const { data: cities } = useFetch("/cities");
  const [f, setF] = useState({ name: "", email: "", phone: "", city_slug: "", consent: false });
  const [done, setDone] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    try { await api.post("/leads", { type: "seminar", ...f, cta: "free-seminar-form", utm: getUtm() }); setDone(true); } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <>
      <PageHero eyebrow="Free" title="Free intro seminar" sub="A 60-minute introduction to the five markets and the Mission Control method. No cost, no pressure." />
      <Section>
        <div className="mx-auto max-w-xl">
          {done ? <div className="panel p-8" data-testid="seminar-success"><CheckCircle size={40} weight="fill" className="text-[#1E8E5A]" /><h2 className="mt-3 font-display text-3xl font-bold uppercase">You're on the list</h2><p className="mt-2 text-[#475569]">We will email you the next seminar date for your city.</p></div> : (
            <form onSubmit={submit} className="panel space-y-4 p-8" data-testid="seminar-form">
              <Input required placeholder="Full name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="seminar-name-input" />
              <Input required type="email" placeholder="Email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="seminar-email-input" />
              <Input placeholder="Phone (optional)" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} data-testid="seminar-phone-input" />
              <select required value={f.city_slug} onChange={(e) => setF({ ...f, city_slug: e.target.value })} data-testid="seminar-city-select" className="h-10 w-full rounded-md border border-input bg-white px-3 text-sm">
                <option value="">Choose your city</option>
                {(cities || []).map((c) => <option key={c.slug} value={c.slug}>{c.name}, {c.province}</option>)}
              </select>
              <label className="flex items-start gap-2 text-xs text-[#475569]"><input type="checkbox" checked={f.consent} onChange={(e) => setF({ ...f, consent: e.target.checked })} className="mt-0.5" data-testid="seminar-consent-checkbox" />I agree to receive emails about Mission Control Trading. I can unsubscribe at any time.</label>
              <button className="btn-gold w-full" data-testid="seminar-submit-btn">Save my seat</button>
              <RiskNotice />
            </form>
          )}
        </div>
      </Section>
    </>
  );
}

const QUIZ = [
  ["When do you have time to look at charts?", [["Weekday mornings", "forex"], ["Late nights and weekends", "crypto"], ["Around big news days", "metals"], ["At the market open", "indices"], ["Evenings after work, researching", "stocks"]]],
  ["How do you feel about fast price swings?", [["I like steady structure", "forex"], ["Bring it on, with a plan", "crypto"], ["I prefer calm, news-aware moves", "metals"], ["I think big picture", "indices"], ["I want to know the company", "stocks"]]],
  ["What interests you most?", [["Global currencies", "forex"], ["New technology", "crypto"], ["Gold and safe havens", "metals"], ["The whole economy", "indices"], ["Specific businesses", "stocks"]]],
  ["How do you like to learn?", [["Rules and sessions", "forex"], ["Hands-on and quick", "crypto"], ["Calendars and patience", "metals"], ["Trends and patterns", "indices"], ["Research and reports", "stocks"]]],
];
const RESULT = { forex: "FX Pilot", crypto: "Crypto Navigator", metals: "Metals Engineer", indices: "Index Commander", stocks: "Stock Scout" };

export function TraderQuiz() {
  const [i, setI] = useState(0);
  const [picks, setPicks] = useState([]);
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const done = picks.length === QUIZ.length;
  const tally = picks.reduce((a, p) => ({ ...a, [p]: (a[p] || 0) + 1 }), {});
  const result = Object.entries(tally).sort((a, b) => b[1] - a[1])[0]?.[0];
  const send = async (e) => {
    e.preventDefault();
    try { await api.post("/leads", { type: "quiz", email, result, consent: true, cta: "trader-type-quiz", utm: getUtm() }); setSent(true); } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <>
      <PageHero eyebrow="Lead magnet · 1 minute" title="What type of trader are you?" sub="Four quick questions. Get a module recommendation." />
      <Section>
        <div className="mx-auto max-w-2xl panel p-8" data-testid="trader-quiz">
          {!done ? (
            <>
              <div className="font-mono-num text-xs text-[#475569]">Question {i + 1} / {QUIZ.length}</div>
              <h2 className="mt-2 font-display text-3xl font-bold uppercase">{QUIZ[i][0]}</h2>
              <div className="mt-5 space-y-2">
                {QUIZ[i][1].map(([label, v], k) => (
                  <button key={label} onClick={() => { setPicks([...picks, v]); setI(Math.min(i + 1, QUIZ.length - 1)); }} data-testid={`trader-quiz-option-${k}`}
                    className="w-full rounded-md border-2 border-[#0B1B33]/10 px-4 py-3 text-left transition-colors hover:border-[#E0A21B] hover:bg-[#E0A21B]/5">{label}</button>
                ))}
              </div>
            </>
          ) : (
            <div data-testid="trader-quiz-result">
              <div className="eyebrow">Your result</div>
              <h2 className="mt-2 font-display text-5xl font-black uppercase">{RESULT[result]}</h2>
              <p className="mt-2 text-[#475569]">We recommend starting with the {result === "forex" ? "FX" : result} module, taught inside every full-day Mission Control class.</p>
              <div className="mt-5 flex flex-wrap gap-3"><Link to={`/courses/${result}`} className="btn-gold" data-testid="quiz-result-course-btn">See the module</Link><button className="btn-outline" onClick={() => { setPicks([]); setI(0); }} data-testid="quiz-retake-btn">Retake</button></div>
              {sent ? <p className="mt-6 text-sm text-[#1E8E5A]" data-testid="quiz-email-sent">Result sent to your inbox.</p> : (
                <form onSubmit={send} className="mt-6 flex gap-2">
                  <Input required type="email" placeholder="Email me my result" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="quiz-email-input" />
                  <button className="btn-navy" data-testid="quiz-email-btn">Send</button>
                </form>
              )}
            </div>
          )}
          <RiskNotice className="mt-6" />
        </div>
      </Section>
    </>
  );
}

export function Faq() {
  const { data, loading } = useFetch("/faqs");
  const [q, setQ] = useState("");
  const rows = (data || []).filter((f) => (f.question + f.answer).toLowerCase().includes(q.toLowerCase()));
  return (
    <>
      <PageHero eyebrow="Help" title="Questions, answered" />
      <Section>
        <div className="mx-auto max-w-3xl">
          <IndicatorNotice testid="indicator-notice-faq" className="mb-6" />
          <div className="mb-6 flex items-center gap-2 rounded-md border bg-white px-3"><MagnifyingGlass className="text-[#475569]" /><input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search questions" className="w-full py-3 focus:outline-none" data-testid="faq-search-input" /></div>
          {loading ? <Loader /> : (
            <Accordion type="single" collapsible className="panel px-6">
              {rows.map((f) => (
                <AccordionItem key={f.id} value={f.id}>
                  <AccordionTrigger className="text-left" data-testid={`faq-item-${f.id}`}>{f.question}</AccordionTrigger>
                  <AccordionContent className="text-[#475569]">{f.answer}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          )}
          <p className="mt-6 text-sm">Still stuck? <Link to="/contact" className="font-semibold underline">Contact support</Link></p>
        </div>
      </Section>
    </>
  );
}

export function Contact() {
  const [f, setF] = useState({ name: "", email: "", subject: "", message: "" });
  const [num, setNum] = useState(null);
  const submit = async (e) => {
    e.preventDefault();
    try { const { data } = await api.post("/contact", f); setNum(data.number); } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <>
      <PageHero eyebrow="Contact" title="Talk to mission control" sub="Messages go straight to our support desk. We reply by email." />
      <Section>
        <div className="mx-auto max-w-xl">
          {num ? <div className="panel p-8" data-testid="contact-success"><h2 className="font-display text-3xl font-bold uppercase">Message received</h2><p className="mt-2 text-[#475569]">Your ticket number is <span className="font-mono-num font-semibold">#{num}</span>. We will reply by email.</p></div> : (
            <form onSubmit={submit} className="panel space-y-4 p-8" data-testid="contact-form">
              <Input required placeholder="Name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="contact-name-input" />
              <Input required type="email" placeholder="Email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="contact-email-input" />
              <Input required placeholder="Subject" value={f.subject} onChange={(e) => setF({ ...f, subject: e.target.value })} data-testid="contact-subject-input" />
              <Textarea required rows={5} placeholder="How can we help?" value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} data-testid="contact-message-input" />
              <button className="btn-gold w-full" data-testid="contact-submit-btn">Send message</button>
              <p className="text-xs text-[#475569]">[Placeholder: business email and phone number]</p>
            </form>
          )}
        </div>
      </Section>
    </>
  );
}

export function LegalPage({ slug }) {
  const { data, loading } = useFetch(`/pages/${slug}`);
  if (loading) return <Loader />;
  return (
    <>
      <PageHero eyebrow="Legal" title={data?.title || "Page"} sub={data ? `Version ${data.version} · Updated ${fmtDate(data.updated_at)}` : ""} />
      <Section tone="white"><div className="max-w-3xl whitespace-pre-line text-[#334155]" data-testid="legal-body">{data?.body}</div></Section>
    </>
  );
}

export function Verify() {
  const { code } = useParams();
  const nav = useNavigate();
  const [val, setVal] = useState(code || "");
  const { data, error, loading } = useFetch(code ? `/verify/${code}` : null);
  return (
    <>
      <PageHero eyebrow="Certificate check" title="Verify a certificate" />
      <Section>
        <div className="mx-auto max-w-xl space-y-4">
          <form onSubmit={(e) => { e.preventDefault(); nav(`/verify/${val.trim()}`); }} className="flex gap-2">
            <Input placeholder="MCT-C-XXXXXXXX" value={val} onChange={(e) => setVal(e.target.value)} data-testid="verify-code-input" />
            <button className="btn-navy" data-testid="verify-submit-btn">Check</button>
          </form>
          {code && !loading && (data ? (
            <div className="panel border-l-4 border-l-[#1E8E5A] p-6" data-testid="verify-valid">
              <SealCheck size={40} weight="fill" className="text-[#1E8E5A]" />
              <h2 className="mt-2 font-display text-3xl font-bold uppercase">Valid certificate</h2>
              <p className="mt-1"><strong>{data.student_name}</strong> completed <strong>{data.program_name}</strong> on {fmtDate(data.issued_at)}.</p>
            </div>
          ) : <div className="panel border-l-4 border-l-[#C8382E] p-6" data-testid="verify-invalid">{error}</div>)}
        </div>
      </Section>
    </>
  );
}

export function NotFound() {
  return <Section><h1 className="font-display text-6xl font-black uppercase">Lost in space</h1><p className="mt-2 text-[#475569]">This page does not exist.</p><Link to="/" className="btn-gold mt-6">Back to base</Link></Section>;
}
